import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, Field } from "@/components/ui-hud";
import AirportInput from "@/components/AirportInput";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { Building2, Star as StarIcon, ArrowLeft, Ticket, Check, X as XIcon, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// Personal roles a single human can wear. Business/entity roles (charter operator,
// FBO) live on their OWN account and use /operators/onboard — NOT this form.
const PERSONAL_ROLES = [
  { key: "pilot",            label: "Pilot",              helper: "" },
  { key: "flight_attendant", label: "Flight Attendant",   helper: "" },
  { key: "amt",              label: "AMT (A&P)",          helper: "" },
  { key: "aircraft_owner",   label: "Aircraft Owner",     helper: "" },
  { key: "aircraft_sales",   label: "Sales / Broker",     helper: "" },
  { key: "enthusiast",       label: "AV/EXEC",             helper: "" },
  { key: "charter_client",   label: "Charter Client",     helper: "For flyers who charter — unlocks operator-direct bids · $5k/yr" },
];

export default function AuthPage({ mode = "login" }) {
  const nav = useNavigate();
  const routerLocation = useLocation();
  const nextParam = new URLSearchParams(routerLocation.search).get("next");
  const { login, register } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "", role: "", additional_roles: [], home_base_city: "", discount_code: "" });
  const [busy, setBusy] = useState(false);
  // Discount code UX state — collapsed by default so it doesn't clutter
  // regular signups; only appears if the pilot has been issued a code.
  const [dcOpen, setDcOpen] = useState(false);
  const [dcStatus, setDcStatus] = useState(null); // { ok, tier?, duration_days?, reason? }
  const [dcChecking, setDcChecking] = useState(false);

  const isRegister = mode === "register";

  const togglePersonalRole = (k) => {
    setForm((f) => {
      // Case 1: nothing selected yet — this becomes primary.
      if (!f.role) return { ...f, role: k, additional_roles: [] };
      // Case 2: clicking the primary — clear it (and let user pick again).
      if (f.role === k) {
        // Promote the first extra to primary; else leave role empty.
        const [next, ...rest] = f.additional_roles;
        return { ...f, role: next || "", additional_roles: next ? rest : [] };
      }
      // Case 3: toggle an extra on/off.
      const has = f.additional_roles.includes(k);
      return { ...f, additional_roles: has ? f.additional_roles.filter((r) => r !== k) : [...f.additional_roles, k] };
    });
  };

  const setPrimary = (k) => {
    setForm((f) => {
      if (f.role === k) return f;
      const nextExtras = [...f.additional_roles.filter((r) => r !== k)];
      if (f.role) nextExtras.push(f.role); // demote current primary to extras
      return { ...f, role: k, additional_roles: nextExtras };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isRegister && !form.role) {
      toast.error("Pick at least one hat before continuing");
      return;
    }
    setBusy(true);
    try {
      let newUser = null;
      if (isRegister) newUser = await register(form);
      else await login(form.email, form.password);
      // Discount code applied? Show a distinct green toast so the pilot
      // knows their Pro access is live and no Stripe checkout is coming.
      const dc = newUser?.discount;
      if (dc?.applied) {
        toast.success(`Comp code applied — ${(dc.tier || "PJP Pro").replace(/_/g, " ").toUpperCase()} unlocked. Welcome aboard.`);
        // Killswitch any lingering Stripe intent — the pilot is already in.
        sessionStorage.removeItem("pjp_pending_checkout");
      } else {
        toast.success(isRegister ? "Welcome to PJP" : "Welcome back");
      }

      // If the shopper stashed a pending RFQ before hitting the upsell page,
      // patch it with their real contact details now that we have them.
      // We DON'T fire it here yet — we let PaymentSuccess.jsx do that after
      // the Stripe charge clears (so operators aren't spammed until the
      // customer actually paid). For non-paid flows (adding the charter_client
      // hat to an existing account) we still fire on login.
      try {
        const { peekPendingRfq, stashPendingRfq, replayPendingRfq } = await import("@/lib/pendingRfq");
        const p = peekPendingRfq();
        if (p) {
          const contactName = [form.first_name, form.last_name].filter(Boolean).join(" ").trim() || p.contact_name;
          stashPendingRfq({ ...p, contact_name: contactName, contact_email: form.email });
          // If there's no pending Stripe checkout waiting, this is a returning
          // charter-client member logging in — replay immediately.
          if (!sessionStorage.getItem("pjp_pending_checkout")) {
            await replayPendingRfq();
          }
        }
      } catch { /* non-blocking */ }

      // Resume pending Stripe checkout if the user was redirected here from
      // Membership / EmptyLegProgram. Routes through the Digital ID upsell
      // interstitial so every new signup gets the one-click addon offer.
      // Skipped entirely when a comp code was applied — those signups already
      // have their tier unlocked and shouldn't be re-charged.
      const pending = sessionStorage.getItem("pjp_pending_checkout");
      if (pending && !dc?.applied) {
        sessionStorage.removeItem("pjp_pending_checkout");
        // Map lookup_key -> tier slug so the interstitial can render correctly.
        const { MEMBERSHIP_TIERS } = await import("@/data/pjp");
        const EXTRA = { pjp_empty_leg_rider_yearly: "empty-leg-rider" };
        const match = MEMBERSHIP_TIERS.find((t) => t.lookup_key === pending);
        const tierSlug = match?.slug || EXTRA[pending] || null;
        if (tierSlug) {
          nav(`/membership/addon?tier=${tierSlug}`);
          return;
        }
        // Unknown lookup_key — fall back to the pre-interstitial direct flow.
        try {
          const { api } = await import("@/lib/api");
          const { redirectExternal } = await import("@/lib/redirect");
          const r = await api.post("/payments/checkout", {
            lookup_key: pending,
            origin_url: window.location.origin,
          });
          if (redirectExternal(r.data.checkout_url)) return;
          nav("/membership");
          return;
        } catch {
          nav("/membership");
          return;
        }
      }
      // If we came from a gated page (e.g. /search), bounce back there.
      if (nextParam && nextParam.startsWith("/")) {
        nav(nextParam);
        return;
      }
      // Wow-moment: if the fresh registrant got a home ICAO, drop them onto the globe
      if (isRegister && newUser?.home_base_icao) {
        nav(`/airports/${newUser.home_base_icao}?welcome=1`);
        return;
      }
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 hud-grid">
      <div className="w-full max-w-md">
        {/* Back button — history.back() when there is a referrer, otherwise home */}
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) nav(-1);
            else nav("/");
          }}
          data-testid="auth-back-btn"
          className="mb-6 inline-flex items-center gap-2 text-white/55 hover:text-white text-[12px] tracking-widest uppercase mono transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <div className="overline text-cyan mb-3">// {isRegister ? "CREATE ACCOUNT" : "IDENTITY CHECK"}</div>
        <h1 className="font-display text-4xl font-bold mb-8 tracking-tight">
          {isRegister ? "Join PJP." : "Sign in."}
        </h1>
        <Card>
          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name">
                  <input data-testid="input-first-name" required className="input-hud" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                </Field>
                <Field label="Last name">
                  <input data-testid="input-last-name" required className="input-hud" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                </Field>
              </div>
            )}
            <Field label="Email">
              <input data-testid="input-email" type="email" required className="input-hud" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Password">
              <PasswordInput data-testid="input-password" required minLength={6} className="input-hud" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete={isRegister ? "new-password" : "current-password"} />
            </Field>
            {isRegister && (
              <Field label="Which hats do you wear?">
                <div data-testid="roles-picker">
                  <div className="text-[11px] text-white/45 mb-3">
                    Tap every hat you wear. The <span className="text-cyan">first one you tap</span> becomes
                    your primary — tap the <span className="mono text-white/70">★</span> on any other to promote it.
                    One PJP membership covers every personal hat.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {PERSONAL_ROLES.map((r) => {
                      const isPrimary = form.role === r.key;
                      const isExtra = form.additional_roles.includes(r.key);
                      const isChecked = isPrimary || isExtra;
                      return (
                        <button
                          key={r.key}
                          type="button"
                          onClick={() => togglePersonalRole(r.key)}
                          data-testid={`role-row-${r.key}`}
                          className={`group inline-flex items-center gap-2 pl-3 pr-2 py-2 rounded-full border text-sm transition-all ${
                            isPrimary
                              ? "border-cyan bg-cyan/[0.18] text-white shadow-[0_0_18px_rgba(75,156,255,0.35)]"
                              : isExtra
                                ? "border-cyan/40 bg-cyan/[0.06] text-white"
                                : "border-white/[0.12] bg-white/[0.02] text-white/70 hover:border-white/25 hover:text-white"
                          }`}
                        >
                          <span
                            data-testid={`role-check-${r.key}`}
                            aria-checked={isChecked}
                            role="checkbox"
                            className={`inline-flex h-4 w-4 items-center justify-center rounded-[3px] border ${
                              isChecked ? "bg-cyan border-cyan" : "border-white/40"
                            }`}
                          >
                            {isChecked && (
                              <svg viewBox="0 0 20 20" className="h-3 w-3 text-obsidian" fill="currentColor">
                                <path d="M8.3 13.3l-3-3 1.4-1.4 1.6 1.6 4.6-4.6 1.4 1.4z" />
                              </svg>
                            )}
                          </span>
                          <span className="whitespace-nowrap">{r.label}</span>
                          {isPrimary ? (
                            <span
                              data-testid={`role-primary-${r.key}`}
                              className="ml-1 mono text-[9px] tracking-widest text-cyan inline-flex items-center gap-1"
                            >
                              <StarIcon className="h-3 w-3 fill-cyan text-cyan" /> PRIMARY
                            </span>
                          ) : (
                            isExtra && (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); setPrimary(r.key); }}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setPrimary(r.key); } }}
                                data-testid={`role-set-primary-${r.key}`}
                                title="Make primary"
                                className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-white/40 hover:text-cyan hover:bg-white/[0.06] cursor-pointer"
                              >
                                <StarIcon className="h-3.5 w-3.5" />
                              </span>
                            )
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {!form.role && (
                    <div className="mt-2 text-[11px] text-orange/80" data-testid="roles-missing-hint">
                      Pick at least one hat to continue.
                    </div>
                  )}
                </div>
              </Field>
            )}

            {isRegister && (
              <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3 flex items-start gap-3" data-testid="entity-cta">
                <Building2 className="h-4 w-4 text-cyan mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-[12px] text-white/85">Registering a Charter Operator or FBO?</div>
                  <div className="text-[11px] text-white/50 mt-0.5">
                    Businesses (fleet, insurance, ARGUS/Wyvern, staff) live on a separate entity account.
                    Create your personal account here first, then <Link to="/operators/onboard" className="text-cyan hover:text-white">launch your company entity</Link>.
                  </div>
                </div>
              </div>
            )}
            {isRegister && (
              /* Secret comp code — collapsed until the user needs it.
                 Debounced live validation calls /auth/discount-codes/validate
                 so pilots get instant "green check" feedback before signup. */
              <div className="rounded-lg border border-avail/25 bg-avail/[0.04] p-3" data-testid="dc-block">
                {!dcOpen ? (
                  <button
                    type="button"
                    onClick={() => setDcOpen(true)}
                    data-testid="dc-toggle-open"
                    className="w-full text-left flex items-center gap-2 text-[12.5px] text-avail hover:text-white transition-colors"
                  >
                    <Ticket className="h-3.5 w-3.5" />
                    <span>Have a secret PJP comp code? <span className="text-white/50">Apply it here</span></span>
                  </button>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="mono text-[10px] tracking-widest uppercase text-avail flex items-center gap-1.5">
                        <Ticket className="h-3 w-3" /> Secret comp code
                      </label>
                      <button
                        type="button"
                        onClick={() => { setDcOpen(false); setForm((f) => ({ ...f, discount_code: "" })); setDcStatus(null); }}
                        data-testid="dc-toggle-close"
                        className="mono text-[10px] text-white/50 hover:text-white"
                      >
                        remove
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        data-testid="input-discount-code"
                        value={form.discount_code}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          setForm((f) => ({ ...f, discount_code: v }));
                          setDcStatus(null);
                          if (window.__dc_t) clearTimeout(window.__dc_t);
                          if (!v.trim()) return;
                          setDcChecking(true);
                          window.__dc_t = setTimeout(async () => {
                            try {
                              const r = await api.post("/auth/discount-codes/validate", { code: v.trim() });
                              setDcStatus(r.data);
                            } catch {
                              setDcStatus({ ok: false, reason: "invalid" });
                            } finally { setDcChecking(false); }
                          }, 400);
                        }}
                        placeholder="PJP-JORDEN-2026"
                        className="w-full bg-white/[0.03] border border-avail/40 rounded-lg px-3 py-2 pr-9 text-sm text-white font-mono tracking-wider focus:outline-none focus:border-avail"
                        autoComplete="off"
                        spellCheck="false"
                      />
                      <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                        {dcChecking ? (
                          <Loader2 className="h-4 w-4 text-white/40 animate-spin" />
                        ) : dcStatus?.ok ? (
                          <Check className="h-4 w-4 text-avail" data-testid="dc-check-ok" />
                        ) : dcStatus && !dcStatus.ok ? (
                          <XIcon className="h-4 w-4 text-red-400" data-testid="dc-check-bad" />
                        ) : null}
                      </div>
                    </div>
                    {dcStatus?.ok && (
                      <div className="mt-2 text-[11.5px] text-avail" data-testid="dc-check-msg">
                        ✓ Valid — unlocks <span className="font-bold uppercase">{(dcStatus.tier || "pjp_pro").replace(/_/g, " ")}</span> for {dcStatus.duration_days} days on signup. No payment required.
                      </div>
                    )}
                    {dcStatus && !dcStatus.ok && form.discount_code && (
                      <div className="mt-2 text-[11.5px] text-red-400" data-testid="dc-check-msg">
                        {dcStatus.reason === "disabled" ? "This code has been disabled."
                          : dcStatus.reason === "exhausted" ? "This code has already been used up."
                          : "Not a valid code."}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {isRegister && (
              <Field label="Home Base — city or ICAO">
                <AirportInput
                  testId="input-home-base"
                  value={form.home_base_city}
                  onChange={(v) => setForm({ ...form, home_base_city: v })}
                  placeholder="Miami · KOPF · London · EGLL"
                />
                <div className="mt-1 text-[11px] text-white/45">We'll drop a pin for you on the PJP global map — you'll see it right after signup.</div>
              </Field>
            )}
            <button data-testid="btn-auth-submit" disabled={busy || (isRegister && !form.role)} className="btn btn-primary w-full justify-center">
              {busy ? "..." : isRegister ? "CREATE ACCOUNT" : "SIGN IN"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="mono text-[10px] text-white/40 tracking-widest">OR</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button
            type="button"
            data-testid="btn-google-auth"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="btn w-full justify-center"
          >
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09A6.5 6.5 0 015.5 12c0-.72.12-1.42.34-2.09V7.07H2.18a11 11 0 000 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>

          <div className="mt-6 text-center eyebrow-muted">
            {isRegister ? (
              <Link to={`/login${nextParam ? `?next=${encodeURIComponent(nextParam)}` : ""}`} data-testid="link-login">Already flying with us? Sign in</Link>
            ) : (
              <Link to={`/register${nextParam ? `?next=${encodeURIComponent(nextParam)}` : ""}`} data-testid="link-register">New to PJP? Join us</Link>
            )}
          </div>

          {/* Legacy-site subscriber path — surface on both login + register so
              pilots migrating from the old privatejetpilots.com can bring
              their existing Stripe subscription across without paying twice. */}
          <div className="mt-3 text-center">
            <Link
              to="/claim"
              data-testid="link-claim-legacy"
              className="mono text-[10px] tracking-widest uppercase text-avail hover:text-white transition-colors inline-flex items-center gap-1"
            >
              🛬 Migrating from the old site? Claim your subscription
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
